/** Wholesale Clients page — dedicated view for wholesale customer accounts.
 *
 * Full CRUD against /api/v1/accounts (create, PATCH edit, delete),
 * real rate-plan assignment via /api/v1/rate-plans/assign,
 * balance top-up via /api/v1/accounts/{id}/balance, and a working
 * View-CDRs deep link (/cdrs?account={tenant}:{account}).
 */
import React, { useMemo, useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add as AddIcon,
  Upload as UploadIcon,
  AccountBalanceWallet as TopUpIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import AccountTable, { Account, CommonColumns } from "../../components/accounts/AccountTable";
import { ColumnDef } from "@tanstack/react-table";
import apiClient from "../../api/client";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import ImportAccountsDialog from "../../components/accounts/ImportAccountsDialog";
import { useNotify } from "../../components/common/NotificationProvider";

/** Minimal rate-plan shape for the assignment dropdown. */
interface RatePlanOption {
  id: string;
  name: string;
}

/** Breadcrumb component. */
function WholesaleBreadcrumbs() {
  const navigate = useNavigate();

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
      <Link
        underline="hover"
        color="inherit"
        href="/"
        onClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
      >
        Home
      </Link>
      <Link
        underline="hover"
        color="inherit"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          navigate("/accounts");
        }}
      >
        Customers
      </Link>
      <Typography color="text.primary">Wholesale Clients</Typography>
    </Breadcrumbs>
  );
}

/** Form state for the add/edit dialog. */
interface ClientForm {
  account: string;
  parent_id: string;
  company: string;
  email: string;
  credit_limit: string;
  account_type: "*prepaid" | "*postpaid";
  rate_plan_id: string;
  balance: string;
}

const EMPTY_FORM: ClientForm = {
  account: "",
  parent_id: "",
  company: "",
  email: "",
  credit_limit: "",
  account_type: "*postpaid",
  rate_plan_id: "",
  balance: "",
};

/** Wholesale client create/edit dialog. Edits are prefilled from the
 * selected client (account name is immutable after creation). */
interface AddClientDialogProps {
  open: boolean;
  editClient: Account | null;
  ratePlans: RatePlanOption[];
  resellers: { id: string; account: string; level?: number }[];
  onClose: () => void;
  onSave: (data: ClientForm) => void;
}

function AddClientDialog({ open, editClient, ratePlans, resellers, onClose, onSave }: AddClientDialogProps) {
  const [formData, setFormData] = useState<ClientForm>(EMPTY_FORM);

  /** Prefill from the client being edited (or reset for create). */
  useEffect(() => {
    if (!open) return;
    if (editClient) {
      setFormData({
        account: editClient.account,
        parent_id: (editClient as Account & { parent_id?: string }).parent_id ?? "",
        company: (editClient as Account & { company?: string }).company ?? "",
        email: (editClient as Account & { email?: string }).email ?? "",
        credit_limit: editClient.credit_limit != null ? String(editClient.credit_limit) : "",
        account_type: (editClient.account_type as "*prepaid" | "*postpaid") || "*postpaid",
        rate_plan_id: (editClient as Account & { rate_plan_id?: string }).rate_plan_id ?? "",
        balance: "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [open, editClient]);

  const handleChange = (field: keyof ClientForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = () => {
    if (!formData.account) return;
    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editClient ? `Edit Wholesale Client: ${editClient.account}` : "Add Wholesale Client"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Account Name"
            fullWidth
            required
            value={formData.account}
            onChange={handleChange("account")}
            placeholder="e.g., acme_wholesale"
            helperText={editClient ? "Account name cannot be changed" : "Internal billing account ID"}
            disabled={!!editClient}
          />
          <TextField
            label="Owner (Reseller)"
            fullWidth
            select
            value={formData.parent_id}
            onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
            disabled={!!editClient}
            helperText={
              editClient
                ? "Ownership cannot be changed"
                : "Optional — leave empty for a brand-owned client, or select the reseller that owns this client"
            }
          >
            <MenuItem value="">— Brand (direct customer) —</MenuItem>
            {resellers.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.account} (L{r.level ?? 1})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Company Name"
            fullWidth
            value={formData.company}
            onChange={handleChange("company")}
            placeholder="e.g., Acme Telecom Corp"
          />
          <TextField
            label="Contact Email"
            fullWidth
            type="email"
            value={formData.email}
            onChange={handleChange("email")}
            placeholder="billing@acme.example"
          />
          <FormControl fullWidth>
            <InputLabel>Billing Type</InputLabel>
            <Select
              value={formData.account_type}
              label="Billing Type"
              onChange={(e) =>
                setFormData({ ...formData, account_type: e.target.value as "*prepaid" | "*postpaid" })
              }
            >
              <MenuItem value="*postpaid">Postpaid (credit limit)</MenuItem>
              <MenuItem value="*prepaid">Prepaid (balance)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Credit Limit"
            fullWidth
            type="number"
            value={formData.credit_limit}
            onChange={handleChange("credit_limit")}
            placeholder="e.g., 10000.00"
            InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
          />
          <TextField
            label="Initial Balance"
            fullWidth
            type="number"
            value={formData.balance}
            onChange={handleChange("balance")}
            placeholder="e.g., 500.00"
            disabled={!!editClient}
            helperText={editClient ? "Use Top Up action to change balance" : "Only for prepaid accounts"}
            InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
          />
          <TextField
            label="Rate Plan"
            fullWidth
            select
            value={formData.rate_plan_id}
            onChange={(e) => setFormData({ ...formData, rate_plan_id: e.target.value })}
            helperText={
              ratePlans.length === 0
                ? "No rate plans defined yet — create one under Rate Plans"
                : "Customer-facing rates applied to this client's calls"
            }
          >
            <MenuItem value="">— None —</MenuItem>
            {ratePlans.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!formData.account}
        >
          {editClient ? "Save Changes" : "Add Client"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Authorized source-IP management for a wholesale client (inbound IP auth). */
interface IpDialogProps {
  open: boolean;
  client: Account | null;
  onClose: () => void;
  onSync: () => void;
}

interface IpEntry {
  id: number;
  ip_address: string;
  description: string | null;
  enabled: boolean;
}

function IpAuthDialog({ open, client, onClose, onSync }: IpDialogProps) {
  const notify = useNotify();
  const [ips, setIps] = useState<IpEntry[]>([]);
  const [newIp, setNewIp] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!client) return;
    try {
      const res = await apiClient.get(`/api/v1/accounts/${encodeURIComponent(client.id)}/ips`);
      setIps(res.data.ips || []);
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to load IPs", "error");
    }
  }, [client, notify]);

  useEffect(() => {
    if (open) {
      setNewIp("");
      setDesc("");
      load();
    }
  }, [open, load]);

  const add = async () => {
    if (!newIp.trim() || !client) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/v1/accounts/${encodeURIComponent(client.id)}/ips`, {
        ip_address: newIp.trim(),
        description: desc.trim() || undefined,
      });
      setNewIp("");
      setDesc("");
      await load();
      onSync();
      notify("IP added and synced to Kamailio");
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to add IP", "error");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ipId: number) => {
    if (!client) return;
    setBusy(true);
    try {
      await apiClient.delete(`/api/v1/accounts/${encodeURIComponent(client.id)}/ips/${ipId}`);
      await load();
      onSync();
      notify("IP removed and synced to Kamailio");
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to remove IP", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Authorized Source IPs — {client?.account}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Calls from these IPs are authenticated as this account at the SIP edge
          (Kamailio) and billed against it. Single IPs or CIDRs (e.g. 10.0.0.0/24).
        </Typography>
        {ips.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No IPs authorized yet — this carrier cannot send calls.
          </Typography>
        ) : (
          <Box sx={{ mb: 2 }}>
            {ips.map((ip) => (
              <Box
                key={ip.id}
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", flex: 1 }}
                >
                  {ip.ip_address}
                  {ip.description ? ` — ${ip.description}` : ""}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() => remove(ip.id)}
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Box>
        )}
        <TextField
          label="IP or CIDR"
          size="small"
          fullWidth
          value={newIp}
          onChange={(e) => setNewIp(e.target.value)}
          placeholder="e.g. 203.0.113.7 or 203.0.113.0/24"
          sx={{ mb: 1 }}
        />
        <TextField
          label="Description (optional)"
          size="small"
          fullWidth
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="e.g. London PoP SBC"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={add}
          variant="contained"
          disabled={!newIp.trim() || busy}
          startIcon={<AddIcon />}
        >
          Authorize IP
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Balance top-up dialog (POST /accounts/{id}/balance). */interface TopUpDialogProps {
  open: boolean;
  client: Account | null;
  onClose: () => void;
  onConfirm: (amount: number, reason: string) => Promise<void>;
}

function TopUpDialog({ open, client, onClose, onConfirm }: TopUpDialogProps) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount("");
      setReason("");
    }
  }, [open]);

  const handleConfirm = async () => {
    const value = parseFloat(amount);
    if (!value) return;
    setWorking(true);
    try {
      await onConfirm(value, reason.trim());
      onClose();
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Top Up Balance — {client?.account}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Amount (negative to deduct)"
            fullWidth
            required
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{ startAdornment: <Box sx={{ mr: 1 }}>$</Box> }}
            autoFocus
          />
          <TextField
            label="Reason"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., wire transfer ref #1234"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!amount || working}
          startIcon={working ? <CircularProgress size={16} /> : <TopUpIcon />}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Wholesale Clients page. */
export default function WholesaleClients() {
  const notify = useNotify();
  const [clients, setClients] = useState<Account[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlanOption[]>([]);
  const [resellers, setResellers] = useState<{ id: string; account: string; level?: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editClient, setEditClient] = useState<Account | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Account | null>(null);
  const [topUpClient, setTopUpClient] = useState<Account | null>(null);
  const [ipClient, setIpClient] = useState<Account | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const navigate = useNavigate();

  /** Fetch wholesale clients from API. */
  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/api/v1/accounts?type=wholesale");
      setClients(response.data.accounts || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load wholesale clients");
    } finally {
      setLoading(false);
    }
  }, []);

  /** Fetch resellers for the ownership dropdown. */
  const fetchResellers = useCallback(async () => {
    try {
      const response = await apiClient.get("/api/v1/resellers/", { params: { page: 1, page_size: 500 } });
      const items = response.data?.resellers || [];
      setResellers(items.map((r: any) => ({ id: r.id, account: r.account, level: r.level })));
    } catch {
      // dropdown stays empty
    }
  }, []);

  /** Fetch real rate plans for the assignment dropdown. */
  const fetchRatePlans = useCallback(async () => {
    try {
      const response = await apiClient.get("/api/v1/rate-plans/", { params: { skip: 0, limit: 200 } });
      const items = response.data?.rate_plans || response.data?.items || response.data?.plans || [];
      setRatePlans(
        items.map((p: any) => ({ id: p.id, name: p.name })).filter((p: RatePlanOption) => p.id)
      );
    } catch {
      // Rate plans stay empty; dropdown shows helper text
    }
  }, []);

  useEffect(() => {
    fetchClients();
    fetchRatePlans();
    fetchResellers();
  }, [fetchClients, fetchRatePlans, fetchResellers]);

  /** Table columns for wholesale clients. */
  const columns = useMemo(() => {
    const cols: ColumnDef<Account, any>[] = [
      CommonColumns.accountName,
      {
        accessorKey: "company",
        header: "Company Name",
        cell: (info) => info.getValue() || "-",
        enableSorting: true,
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: (row) => (row as Account & { parent_id?: string }).parent_id || "",
        cell: (info) => {
          const owner = info.getValue() as string;
          if (!owner) return <Chip label="Brand" size="small" variant="outlined" />;
          const r = resellers.find((x) => x.id === owner);
          return <Chip label={r ? r.account : owner} size="small" color="primary" variant="outlined" />;
        },
        enableSorting: false,
      },
      CommonColumns.balance,
      CommonColumns.creditLimit,
      {
        accessorKey: "rate_plan_id",
        header: "Rate Plan",
        cell: (info) => {
          const planId = info.getValue() as string | undefined;
          const plan = ratePlans.find((p) => p.id === planId);
          return plan ? (
            <Chip label={plan.name} size="small" variant="outlined" />
          ) : (
            "-"
          );
        },
        enableSorting: false,
      },
      CommonColumns.accountType,
      CommonColumns.status,
    ];
    return cols;
  }, [ratePlans, resellers]);

  /** Handle edit client (prefills the dialog). */
  const handleEdit = (client: Account) => {
    setEditClient(client);
    setDialogOpen(true);
  };

  /** Handle delete request — confirmed via styled dialog (UX review C2). */
  const handleDelete = (clientId: string) => {
    setPendingDelete(clients.find((c) => c.id === clientId) || null);
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    try {
      await apiClient.delete(`/api/v1/accounts/${encodeURIComponent(pendingDelete.id)}`);
      setPendingDelete(null);
      notify(`Deleted client ${pendingDelete.account}`);
      await fetchClients();
    } catch (err: any) {
      setPendingDelete(null);
      setError(err.response?.data?.detail || "Failed to delete client");
    }
  };

  /** Assign (or replace) the client's rate plan. No-op when planId empty. */
  const assignRatePlan = async (clientId: string, planId: string) => {
    if (!planId) return;
    await apiClient.post("/api/v1/rate-plans/assign", {
      customer_id: clientId,
      rate_plan_id: planId,
      assigned_by: clientId, // self-provisioned by admin on behalf of the client
    });
  };

  /** Handle save client (create or update). */
  const handleSave = async (data: ClientForm) => {
    try {
      let clientId: string;
      if (editClient) {
        clientId = editClient.id;
        await apiClient.patch(`/api/v1/accounts/${encodeURIComponent(editClient.id)}`, {
          company: data.company || null,
          email: data.email || null,
          credit_limit: data.credit_limit === "" ? null : parseFloat(data.credit_limit),
          account_type: data.account_type,
        });
      } else {
        const response = await apiClient.post("/api/v1/accounts", {
          tenant: "b1",
          account: data.account.toLowerCase().replace(/\s+/g, "_"),
          account_type: data.account_type,
          customer_type: "wholesale",
          credit_limit: data.credit_limit === "" ? null : parseFloat(data.credit_limit),
          balance: data.balance === "" ? 0 : parseFloat(data.balance),
          company: data.company || null,
          email: data.email || null,
          parent_id: data.parent_id || undefined,
        });
        clientId = response.data.id;
      }
      await assignRatePlan(clientId, data.rate_plan_id);
      notify(editClient ? "Client updated" : `Client ${data.account} created`);
      setDialogOpen(false);
      setEditClient(null);
      await fetchClients();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save client");
    }
  };

  /** Push wholesale IPs to the edge after IP-list changes. */
  const handleSyncIps = useCallback(async () => {
    try {
      await apiClient.post("/api/v1/accounts/sync-wholesale-ips");
    } catch (err: any) {
      notify(err.response?.data?.detail || "Edge sync failed", "error");
    }
  }, [notify]);

  /** Apply a balance adjustment (top-up / deduct). */
  const handleTopUp = async (amount: number, reason: string) => {
    if (!topUpClient) return;
    try {
      await apiClient.post(`/api/v1/accounts/${encodeURIComponent(topUpClient.id)}/balance`, {
        amount,
        reason: reason || undefined,
      });
      notify(`Balance updated for ${topUpClient.account}`);
      await fetchClients();
    } catch (err: any) {
      notify(err.response?.data?.detail || "Failed to update balance", "error");
      throw err;
    }
  };

  /** Handle view CDRs — deep link the CDRs page with an account filter. */
  const handleViewCDRs = (client: Account) => {
    navigate(`/cdrs?account=${encodeURIComponent(`${client.tenant}:${client.account}`)}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <WholesaleBreadcrumbs />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Wholesale Clients</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportOpen(true)}
          >
            Import Clients
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditClient(null);
              setDialogOpen(true);
            }}
          >
            Add Client
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {clients.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            No wholesale clients found. Click "Add Client" to create your first wholesale
            client.
          </Typography>
        </Paper>
      ) : (
        <AccountTable
          accounts={clients}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewCDRs={handleViewCDRs}
          typeSpecificActions={(client: Account) => (
            <>
              <Button
                size="small"
                startIcon={<TopUpIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  setTopUpClient(client);
                }}
              >
                Top Up
              </Button>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setIpClient(client);
                }}
              >
                IPs
              </Button>
            </>
          )}
        />
      )}

      {/* Add/Edit client dialog */}
      <AddClientDialog
        open={dialogOpen}
        editClient={editClient}
        ratePlans={ratePlans}
        resellers={resellers}
        onClose={() => {
          setDialogOpen(false);
          setEditClient(null);
        }}
        onSave={handleSave}
      />

      {/* Balance top-up dialog */}
      <TopUpDialog
        open={topUpClient !== null}
        client={topUpClient}
        onClose={() => setTopUpClient(null)}
        onConfirm={handleTopUp}
      />

      {/* Bulk CSV import */}
      <ImportAccountsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/api/v1/accounts/import"
        post={(ep, body) => apiClient.post(ep, body)}
        onImported={(created, failed) => {
          notify(`Import finished: ${created} created, ${failed} failed`);
          fetchClients();
        }}
      />

      {/* Authorized source IPs (inbound IP auth) */}
      <IpAuthDialog
        open={ipClient !== null}
        client={ipClient}
        onClose={() => setIpClient(null)}
        onSync={handleSyncIps}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete wholesale client"
        message={`Delete ${pendingDelete?.account ?? "this client"}?`}
        details="The account is disabled in the billing engine. Call history is retained for auditing."
        confirmLabel="Delete"
        severity="error"
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDeleteConfirmed}
      />
    </Box>
  );
}
